/**
 * Studio Cent Douze — homegrown analytics client.
 *
 * Public API:
 *   initAnalytics()                      → boot (idempotent, safe to call anywhere)
 *   trackPageview(path?)                 → force pageview (auto-fired on route change)
 *   trackEvent(name, props?)             → custom event ("brief_start", "cta_click", …)
 *   setConsent(true|false)               → user's cookie choice
 *   getConsent()                         → 'accepted' | 'refused' | null
 *
 * Privacy:
 *   - No IP, no fingerprint. Only anonymous visitor_id (localStorage UUID) + session_id.
 *   - Session resets after 30 min of inactivity.
 *   - Nothing sent until consent === 'accepted'.
 */

import { getAttribution } from "./utmTracking";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api/track`;

const K_VISITOR = "scd_visitor_id";
const K_SESSION = "scd_session";
const K_CONSENT = "scd_consent";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min
const HEARTBEAT_INTERVAL_MS = 15_000;

// -----------------------------------------------------
//                       Helpers
// -----------------------------------------------------
function uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function safeGet(key) {
    try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, val) {
    try { localStorage.setItem(key, val); } catch { /* private mode / quota */ }
}

function getVisitorId() {
    let id = safeGet(K_VISITOR);
    if (!id) {
        id = uuid();
        safeSet(K_VISITOR, id);
    }
    return id;
}

function getSession() {
    const raw = safeGet(K_SESSION);
    const now = Date.now();
    let s = null;
    try { s = raw ? JSON.parse(raw) : null; } catch { s = null; }
    if (s && s.last && now - s.last < SESSION_TIMEOUT_MS && s.id) {
        s.last = now;
        safeSet(K_SESSION, JSON.stringify(s));
        return s;
    }
    // New session
    const fresh = { id: uuid(), start: now, last: now };
    safeSet(K_SESSION, JSON.stringify(fresh));
    return fresh;
}

function detectDevice() {
    const ua = navigator.userAgent || "";
    if (/iPad|Tablet|Nexus 7|Kindle/i.test(ua)) return "tablet";
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
    return "desktop";
}

function baseContext() {
    const attribution = getAttribution();
    return {
        visitor_id: getVisitorId(),
        session_id: getSession().id,
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        language: (navigator.language || "").slice(0, 16) || null,
        device: detectDevice(),
        viewport_w: window.innerWidth || null,
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        gclid: attribution.gclid || null,
        fbclid: attribution.fbclid || null,
    };
}

async function beacon(endpoint, payload) {
    try {
        const body = JSON.stringify(payload);
        // Prefer sendBeacon (survives page unload)
        if (
            navigator.sendBeacon &&
            typeof Blob !== "undefined" &&
            endpoint !== "pageview"
        ) {
            const blob = new Blob([body], { type: "application/json" });
            const ok = navigator.sendBeacon(`${API}/${endpoint}`, blob);
            if (ok) return;
        }
        await fetch(`${API}/${endpoint}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        });
    } catch {
        /* silent — tracking must never break the site */
    }
}

// -----------------------------------------------------
//                       Consent
// -----------------------------------------------------
export function getConsent() {
    return safeGet(K_CONSENT);
}

export function setConsent(accepted) {
    const val = accepted ? "accepted" : "refused";
    safeSet(K_CONSENT, val);
    if (accepted) {
        // Fire a pageview for the current page now that consent is granted
        trackPageview();
    }
    // Notify listeners (banner can hide)
    try {
        window.dispatchEvent(
            new CustomEvent("scd:consent", { detail: val })
        );
    } catch { /* ignore */ }
    return val;
}

function consentGranted() {
    return getConsent() === "accepted";
}

// -----------------------------------------------------
//                       Public API
// -----------------------------------------------------
let bootDone = false;
let hbTimer = null;
let lastTrackedPath = null;

export function trackPageview(overridePath) {
    if (!consentGranted()) return;
    const ctx = baseContext();
    if (overridePath) ctx.path = overridePath;
    if (lastTrackedPath === ctx.path) return; // dedupe repeats during SPA nav
    lastTrackedPath = ctx.path;
    beacon("pageview", ctx);
}

export function trackEvent(name, props = null) {
    if (!consentGranted()) return;
    if (!name) return;
    beacon("event", { ...baseContext(), name: String(name).slice(0, 80), props });
}

function sendHeartbeat() {
    if (!consentGranted()) return;
    const session = getSession();
    const elapsed = Date.now() - session.start;
    beacon("heartbeat", { ...baseContext(), elapsed_ms: elapsed });
}

function startHeartbeats() {
    if (hbTimer) return;
    hbTimer = setInterval(() => {
        if (document.visibilityState === "visible") sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    // Send a last heartbeat on page hide
    window.addEventListener("pagehide", sendHeartbeat, { once: false });
}

function watchRouteChanges() {
    // Detect history changes for React Router SPA nav
    const push = history.pushState;
    const replace = history.replaceState;
    const notify = () => setTimeout(() => trackPageview(), 0);
    history.pushState = function (...args) {
        push.apply(this, args);
        notify();
    };
    history.replaceState = function (...args) {
        replace.apply(this, args);
        notify();
    };
    window.addEventListener("popstate", notify);
}

export function initAnalytics() {
    if (bootDone) return;
    bootDone = true;
    // Prime IDs (creates them if missing) so consent-related visits are grouped
    getVisitorId();
    getSession();

    // If already consented from a previous visit, start tracking now
    if (consentGranted()) {
        trackPageview();
        startHeartbeats();
    }
    watchRouteChanges();

    // Whenever consent is granted later, start the loop
    window.addEventListener("scd:consent", (e) => {
        if (e.detail === "accepted") {
            startHeartbeats();
        }
    });

    // Debug helper
    if (typeof window !== "undefined") {
        window.__scdAnalytics = { trackPageview, trackEvent, getConsent, setConsent };
    }
}
